import type { DrawTool } from '../types/index.ts';

// ── Types ────────────────────────────────────────────────────

interface Shape {
  id: string;
  type: 'rect' | 'button' | 'table' | 'container'
      | 'checkbox' | 'radio' | 'input' | 'dropdown' | 'toggle' | 'slider'
      | 'navbar' | 'modal';
  x: number; y: number; w: number; h: number;
  color: string;
}

interface DragState {
  type: 'move' | 'resize';
  shapeId: string;
  handle?: ResizeHandle;
  startMouse: { x: number; y: number };
  startShape: { x: number; y: number; w: number; h: number };
}

type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

const HANDLE_R = 5;
const MIN_SIZE = 20;
const SHAPE_TOOLS = new Set<DrawTool>([
  'rect', 'button', 'table', 'container',
  'checkbox', 'radio', 'input', 'dropdown', 'toggle', 'slider',
  'navbar', 'modal',
]);

const HANDLE_CURSORS: Record<ResizeHandle, string> = {
  nw: 'nw-resize', n: 'n-resize', ne: 'ne-resize', e: 'e-resize',
  se: 'se-resize', s: 's-resize', sw: 'sw-resize', w: 'w-resize',
};

const uid = () => Math.random().toString(36).slice(2, 9);

// ── Class ────────────────────────────────────────────────────

export class CanvasDrawing {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  // Offscreen canvas for pen/erase strokes only
  private penCanvas: HTMLCanvasElement;
  private penCtx: CanvasRenderingContext2D;

  private tool: DrawTool = 'pen';
  private color = '#0e0e0f';

  // Pen drag state
  private penActive = false;
  private isErasing = false;

  // Shape placement (click-to-place)
  private placing = false;
  private placeStart: { x: number; y: number } | null = null;

  // Placed shapes
  private shapes: Shape[] = [];
  private selectedId: string | null = null;

  // Drag / resize
  private dragState: DragState | null = null;
  private dragMoved = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No 2D context');
    this.ctx = ctx;

    this.penCanvas = document.createElement('canvas');
    const penCtx = this.penCanvas.getContext('2d');
    if (!penCtx) throw new Error('No pen 2D context');
    this.penCtx = penCtx;

    this.resize();
    this.bindEvents();
  }

  // ── Public API ──────────────────────────────────────────────

  resize(): void {
    const parent = this.canvas.parentElement!;
    const toolbar = parent.querySelector('.canvas-toolbar') as HTMLElement | null;
    const footer  = parent.querySelector('.canvas-footer')  as HTMLElement | null;
    const w = parent.clientWidth;
    const h = parent.clientHeight
              - (toolbar?.offsetHeight ?? 0)
              - (footer?.offsetHeight  ?? 0);
    const sw = w > 0 ? w : 600;
    const sh = h > 0 ? h : 400;

    this.canvas.width  = sw; this.canvas.height  = sh;
    this.canvas.style.height = sh + 'px';
    this.penCanvas.width = sw; this.penCanvas.height = sh;
    this.penCtx.lineCap = 'round'; this.penCtx.lineJoin = 'round';
    this.redraw();
  }

  setTool(tool: DrawTool): void {
    if (this.placing) {
      this.placing = false;
      this.placeStart = null;
      this.canvas.classList.remove('is-drawing');
      this.redraw();
    }
    this.tool = tool;
    this.updateCursor();
  }

  setColor(color: string): void { this.color = color; }

  clear(): void {
    this.shapes = [];
    this.selectedId = null;
    this.placing = false;
    this.placeStart = null;
    this.dragState = null;
    this.penActive = false;
    this.isErasing = false;
    this.canvas.classList.remove('is-drawing');
    this.penCtx.clearRect(0, 0, this.penCanvas.width, this.penCanvas.height);
    this.redraw();
  }

  toDataURL(): string {
    // Export without selection handles
    const sel = this.selectedId;
    this.selectedId = null;
    this.redraw();
    const url = this.canvas.toDataURL('image/png');
    this.selectedId = sel;
    this.redraw();
    return url;
  }

  // ── Helpers ─────────────────────────────────────────────────

  private sz(): number {
    const el = document.getElementById('stroke-size') as HTMLSelectElement | null;
    return el ? parseInt(el.value, 10) : 3;
  }

  private pos(e: MouseEvent) {
    const r = this.canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  private updateCursor(pos?: { x: number; y: number }) {
    let cursor = 'crosshair';
    if (this.tool === 'erase') cursor = 'cell';
    else if (this.tool === 'text') cursor = 'text';
    else if (this.tool === 'select') {
      cursor = 'default';
      if (pos) {
        const h = this.hitHandle(pos);
        if (h) cursor = HANDLE_CURSORS[h];
        else if (this.hitShape(pos)) cursor = 'move';
      }
    }
    this.canvas.style.cursor = cursor;
    this.canvas.classList.toggle('is-drawing', this.placing);
  }

  // ── Rendering ───────────────────────────────────────────────

  private redraw(preview?: Shape) {
    const { width: w, height: h } = this.canvas;
    this.ctx.clearRect(0, 0, w, h);
    this.ctx.fillStyle = '#ffffff';
    this.ctx.fillRect(0, 0, w, h);
    // Layer 1: pen strokes
    this.ctx.drawImage(this.penCanvas, 0, 0);
    // Layer 2: placed shapes
    for (const s of this.shapes) this.drawShape(s, s.id === this.selectedId);
    // Layer 3: placement preview
    if (preview) this.drawShape(preview, false, true);
  }

  /** Normalize shape so x/y is top-left and w/h are positive */
  private norm(s: Shape) {
    return {
      x: s.w >= 0 ? s.x : s.x + s.w,
      y: s.h >= 0 ? s.y : s.y + s.h,
      w: Math.abs(s.w),
      h: Math.abs(s.h),
    };
  }

  private drawShape(shape: Shape, selected = false, preview = false) {
    const { ctx } = this;
    const { x, y, w, h } = this.norm(shape);
    const col = shape.color;
    if (w < 2 || h < 2) return;

    ctx.save();
    ctx.strokeStyle = col;
    ctx.lineWidth = 1.5;
    if (preview) { ctx.globalAlpha = 0.5; ctx.setLineDash([4, 3]); }

    switch (shape.type) {
      case 'rect':
        ctx.strokeRect(x, y, w, h);
        break;

      case 'button': {
        const r = Math.min(8, w / 4, h / 3);
        this.roundRect(ctx, x, y, w, h, r);
        ctx.stroke();
        ctx.globalAlpha = preview ? 0.35 : 0.65;
        ctx.fillStyle = col;
        ctx.font = `${Math.min(13, h * 0.45)}px DM Sans, sans-serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('Button', x + w / 2, y + h / 2);
        break;
      }

      case 'table': {
        const rows = 3, cols = 3;
        const rh = h / rows, cw = w / cols;
        ctx.strokeRect(x, y, w, h);
        // Header tint
        ctx.globalAlpha = preview ? 0.08 : 0.07;
        ctx.fillStyle = col;
        ctx.fillRect(x, y, w, rh);
        ctx.globalAlpha = preview ? 0.5 : 1;
        // Row lines
        for (let i = 1; i < rows; i++) {
          ctx.beginPath(); ctx.moveTo(x, y + i * rh); ctx.lineTo(x + w, y + i * rh); ctx.stroke();
        }
        // Column lines
        for (let i = 1; i < cols; i++) {
          ctx.beginPath(); ctx.moveTo(x + i * cw, y); ctx.lineTo(x + i * cw, y + h); ctx.stroke();
        }
        break;
      }

      case 'container':
        ctx.setLineDash(preview ? [4, 3] : [7, 4]);
        ctx.strokeRect(x, y, w, h);
        ctx.setLineDash([]);
        ctx.globalAlpha = preview ? 0.35 : 0.5;
        ctx.fillStyle = col;
        ctx.font = '10px DM Mono, monospace';
        ctx.textAlign = 'left'; ctx.textBaseline = 'top';
        ctx.fillText('Container', x + 6, y + 6);
        break;

      case 'navbar': {
        // Outer bar
        ctx.strokeRect(x, y, w, h);
        // Header fill
        ctx.globalAlpha = preview ? 0.08 : 0.06;
        ctx.fillStyle = col;
        ctx.fillRect(x, y, w, h);
        ctx.globalAlpha = preview ? 0.5 : 1;
        // Logo dot + nav dots
        const dotY = y + h / 2;
        ctx.fillStyle = col;
        ctx.beginPath(); ctx.arc(x + 14, dotY, 5, 0, Math.PI * 2); ctx.fill();
        for (let i = 0; i < 3; i++) {
          const lx = x + 34 + i * 28;
          ctx.fillRect(lx, dotY - 3, 20, 6);
        }
        // Right CTA pill
        const pr = Math.min(5, h / 4);
        this.roundRect(ctx, x + w - 44, y + h / 2 - 8, 36, 16, pr);
        ctx.stroke();
        ctx.font = `${Math.min(9, h * 0.35)}px DM Sans, sans-serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillStyle = col;
        ctx.fillText('Navbar', x + w / 2, y + h / 2);
        break;
      }

      case 'modal': {
        // Shadow hint
        ctx.globalAlpha = preview ? 0.04 : 0.06;
        ctx.fillStyle = col;
        ctx.fillRect(x + 4, y + 4, w, h);
        ctx.globalAlpha = preview ? 0.5 : 1;
        // Body
        const mr = Math.min(8, w / 10);
        this.roundRect(ctx, x, y, w, h, mr);
        ctx.fillStyle = '#fff';
        ctx.fill();
        ctx.stroke();
        // Title bar
        const th = Math.min(32, h * 0.22);
        this.roundRect(ctx, x, y, w, th, mr);
        ctx.globalAlpha = preview ? 0.06 : 0.05;
        ctx.fillStyle = col;
        ctx.fill();
        ctx.globalAlpha = preview ? 0.5 : 1;
        ctx.stroke();
        // Close X
        const cx2 = x + w - 14, cy2 = y + th / 2;
        ctx.strokeStyle = col; ctx.lineWidth = 1.2;
        ctx.beginPath(); ctx.moveTo(cx2 - 4, cy2 - 4); ctx.lineTo(cx2 + 4, cy2 + 4); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(cx2 + 4, cy2 - 4); ctx.lineTo(cx2 - 4, cy2 + 4); ctx.stroke();
        ctx.lineWidth = 1.5;
        ctx.fillStyle = col;
        ctx.font = `${Math.min(11, th * 0.45)}px DM Sans, sans-serif`;
        ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.fillText('Modal', x + 12, y + th / 2);
        break;
      }

      case 'input': {
        const ir = Math.min(4, h / 4);
        this.roundRect(ctx, x, y, w, h, ir);
        ctx.stroke();
        ctx.globalAlpha = preview ? 0.35 : 0.6;
        ctx.fillStyle = col;
        ctx.font = `${Math.min(11, h * 0.55)}px DM Sans, sans-serif`;
        ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.fillText('Placeholder…', x + 8, y + h / 2);
        // Cursor line
        ctx.strokeStyle = col; ctx.lineWidth = 1;
        const curX = x + 8 + ctx.measureText('Placeholder…').width + 2;
        ctx.beginPath(); ctx.moveTo(curX, y + 6); ctx.lineTo(curX, y + h - 6); ctx.stroke();
        break;
      }

      case 'dropdown': {
        const dr = Math.min(4, h / 4);
        this.roundRect(ctx, x, y, w, h, dr);
        ctx.stroke();
        ctx.globalAlpha = preview ? 0.35 : 0.6;
        ctx.fillStyle = col;
        ctx.font = `${Math.min(11, h * 0.55)}px DM Sans, sans-serif`;
        ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.fillText('Select…', x + 8, y + h / 2);
        // Chevron
        const ax = x + w - 14, ay = y + h / 2 - 3;
        ctx.strokeStyle = col; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(ax + 5, ay + 5); ctx.lineTo(ax + 10, ay); ctx.stroke();
        break;
      }

      case 'checkbox': {
        // Box
        const bs = Math.min(w * 0.35, h * 0.8, 18);
        const bx = x, by = y + (h - bs) / 2;
        this.roundRect(ctx, bx, by, bs, bs, 2);
        ctx.stroke();
        // Check inside if not preview
        if (!preview) {
          ctx.strokeStyle = col; ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(bx + bs * 0.22, by + bs * 0.5);
          ctx.lineTo(bx + bs * 0.45, by + bs * 0.72);
          ctx.lineTo(bx + bs * 0.78, by + bs * 0.28);
          ctx.stroke();
        }
        // Label
        ctx.globalAlpha = preview ? 0.35 : 0.7;
        ctx.fillStyle = col;
        ctx.font = `${Math.min(12, h * 0.55)}px DM Sans, sans-serif`;
        ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.fillText('Checkbox label', bx + bs + 8, y + h / 2);
        break;
      }

      case 'radio': {
        const rs = Math.min(w * 0.35, h * 0.8, 18);
        const rx2 = x + rs / 2, ry2 = y + h / 2;
        ctx.beginPath(); ctx.arc(rx2, ry2, rs / 2, 0, Math.PI * 2); ctx.stroke();
        if (!preview) {
          ctx.beginPath(); ctx.arc(rx2, ry2, rs * 0.27, 0, Math.PI * 2);
          ctx.fillStyle = col; ctx.fill();
        }
        ctx.globalAlpha = preview ? 0.35 : 0.7;
        ctx.fillStyle = col;
        ctx.font = `${Math.min(12, h * 0.55)}px DM Sans, sans-serif`;
        ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.fillText('Radio label', x + rs + 8, y + h / 2);
        break;
      }

      case 'toggle': {
        const th2 = Math.min(h, 24);
        const tw = th2 * 2;
        const tx2 = x, ty2 = y + (h - th2) / 2;
        const tr = th2 / 2;
        this.roundRect(ctx, tx2, ty2, tw, th2, tr);
        // Active fill
        ctx.globalAlpha = preview ? 0.08 : 0.15;
        ctx.fillStyle = col; ctx.fill();
        ctx.globalAlpha = preview ? 0.5 : 1;
        ctx.stroke();
        // Knob (right = on)
        ctx.beginPath();
        ctx.arc(tx2 + tw - tr, ty2 + tr, tr - 3, 0, Math.PI * 2);
        ctx.fillStyle = col; ctx.fill();
        // Label
        ctx.globalAlpha = preview ? 0.35 : 0.7;
        ctx.fillStyle = col;
        ctx.font = `${Math.min(12, h * 0.55)}px DM Sans, sans-serif`;
        ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.fillText('Toggle', tx2 + tw + 8, y + h / 2);
        break;
      }

      case 'slider': {
        const cy3 = y + h / 2;
        const trackY = cy3;
        // Track
        ctx.strokeStyle = col; ctx.lineWidth = 2;
        ctx.globalAlpha = preview ? 0.25 : 0.3;
        ctx.beginPath(); ctx.moveTo(x, trackY); ctx.lineTo(x + w, trackY); ctx.stroke();
        // Filled portion
        ctx.globalAlpha = preview ? 0.5 : 1;
        ctx.beginPath(); ctx.moveTo(x, trackY); ctx.lineTo(x + w * 0.6, trackY); ctx.stroke();
        // Thumb
        ctx.beginPath(); ctx.arc(x + w * 0.6, trackY, Math.min(7, h / 2.2), 0, Math.PI * 2);
        ctx.fillStyle = '#fff'; ctx.fill();
        ctx.strokeStyle = col; ctx.lineWidth = 1.5; ctx.stroke();
        break;
      }
    }

    ctx.restore();
    if (selected && !preview) this.drawHandles(shape);
  }

  private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.arcTo(x + w, y, x + w, y + r, r);
    ctx.lineTo(x + w, y + h - r);
    ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.lineTo(x + r, y + h);
    ctx.arcTo(x, y + h, x, y + h - r, r);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
  }

  private drawHandles(shape: Shape) {
    const handles = this.handlePositions(shape);
    const { ctx } = this;
    ctx.save();
    ctx.lineWidth = 1.5;
    for (const p of Object.values(handles)) {
      ctx.beginPath(); ctx.arc(p.x, p.y, HANDLE_R, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff'; ctx.fill();
      ctx.strokeStyle = '#555'; ctx.stroke();
    }
    // Selection outline
    const { x, y, w, h } = this.norm(shape);
    ctx.strokeStyle = 'rgba(80,120,255,0.5)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 2]);
    ctx.strokeRect(x - 1, y - 1, w + 2, h + 2);
    ctx.restore();
  }

  private handlePositions(shape: Shape): Record<ResizeHandle, { x: number; y: number }> {
    const { x, y, w, h } = this.norm(shape);
    const mx = x + w / 2, my = y + h / 2;
    return {
      nw: { x, y },         n: { x: mx, y },       ne: { x: x + w, y },
      e:  { x: x + w, y: my },
      se: { x: x + w, y: y + h }, s: { x: mx, y: y + h }, sw: { x, y: y + h },
      w:  { x, y: my },
    };
  }

  private hitHandle(pos: { x: number; y: number }): ResizeHandle | null {
    const shape = this.shapes.find(s => s.id === this.selectedId);
    if (!shape) return null;
    const handles = this.handlePositions(shape);
    for (const [k, p] of Object.entries(handles) as [ResizeHandle, { x: number; y: number }][]) {
      if (Math.hypot(pos.x - p.x, pos.y - p.y) <= HANDLE_R + 4) return k;
    }
    return null;
  }

  private hitShape(pos: { x: number; y: number }): Shape | null {
    for (let i = this.shapes.length - 1; i >= 0; i--) {
      const { x, y, w, h } = this.norm(this.shapes[i]);
      if (pos.x >= x && pos.x <= x + w && pos.y >= y && pos.y <= y + h) return this.shapes[i];
    }
    return null;
  }

  // ── Events ──────────────────────────────────────────────────

  private bindEvents() {
    this.canvas.addEventListener('mousedown', this.onDown);
    this.canvas.addEventListener('mousemove', this.onMove);
    this.canvas.addEventListener('mouseup',   this.onUp);
    this.canvas.addEventListener('mouseleave', this.onLeave);
    this.canvas.addEventListener('click',        this.onClick);
    this.canvas.addEventListener('contextmenu',  this.onRightClick);
    window.addEventListener('keydown', this.onKey);
  }

  private onDown = (e: MouseEvent) => {
    const p = this.pos(e);

    if (this.tool === 'pen') {
      this.penActive = true;
      this.penCtx.strokeStyle = this.color;
      this.penCtx.lineWidth = this.sz();
      this.penCtx.beginPath();
      this.penCtx.moveTo(p.x, p.y);
      // Immediate dot feedback
      this.penCtx.arc(p.x, p.y, this.sz() / 2, 0, Math.PI * 2);
      this.penCtx.fillStyle = this.color;
      this.penCtx.fill();
      this.penCtx.beginPath();
      this.penCtx.moveTo(p.x, p.y);
      this.redraw();
      return;
    }

    if (this.tool === 'erase') { this.isErasing = true; return; }

    if (this.tool === 'select') {
      const handle = this.hitHandle(p);
      if (handle && this.selectedId) {
        const s = this.shapes.find(sh => sh.id === this.selectedId)!;
        this.dragState = {
          type: 'resize', shapeId: this.selectedId, handle,
          startMouse: p, startShape: { x: s.x, y: s.y, w: s.w, h: s.h },
        };
        this.dragMoved = false;
        return;
      }
      const shape = this.hitShape(p);
      if (shape) {
        this.selectedId = shape.id;
        this.dragState = {
          type: 'move', shapeId: shape.id,
          startMouse: p, startShape: { x: shape.x, y: shape.y, w: shape.w, h: shape.h },
        };
        this.dragMoved = false;
        this.redraw();
      }
    }
  };

  private onMove = (e: MouseEvent) => {
    const p = this.pos(e);
    this.updateCursor(p);

    if (this.tool === 'pen' && this.penActive) {
      this.penCtx.lineTo(p.x, p.y);
      this.penCtx.stroke();
      this.penCtx.beginPath();
      this.penCtx.moveTo(p.x, p.y);
      this.redraw();
      return;
    }

    if (this.tool === 'erase' && this.isErasing) {
      this.penCtx.clearRect(p.x - 14, p.y - 14, 28, 28);
      this.redraw();
      return;
    }

    if (this.dragState) {
      this.dragMoved = true;
      this.applyDrag(p);
      return;
    }

    if (this.placing && SHAPE_TOOLS.has(this.tool) && this.placeStart) {
      this.redraw({
        id: '__preview__',
        type: this.tool as Shape['type'],
        x: this.placeStart.x, y: this.placeStart.y,
        w: p.x - this.placeStart.x, h: p.y - this.placeStart.y,
        color: this.color,
      });
    }
  };

  private onUp = () => {
    this.penActive = false;
    this.isErasing = false;
    this.dragState = null;
  };

  private onLeave = () => {
    this.penActive = false;
    this.isErasing = false;
    // Don't cancel drag on leave — user may re-enter
  };

  private onClick = (e: MouseEvent) => {
    const p = this.pos(e);

    if (this.tool === 'text') {
      const text = prompt('Enter label text:');
      if (text) {
        this.penCtx.font = `${12 + this.sz() * 2}px DM Sans, sans-serif`;
        this.penCtx.fillStyle = this.color;
        this.penCtx.fillText(text, p.x, p.y);
        this.redraw();
      }
      return;
    }

    if (this.tool === 'select') {
      if (this.dragMoved) { this.dragMoved = false; return; }
      const shape = this.hitShape(p);
      this.selectedId = shape?.id ?? null;
      this.redraw();
      return;
    }

    if (SHAPE_TOOLS.has(this.tool)) {
      if (!this.placing) {
        // First click — record start, show indicator
        this.placing = true;
        this.placeStart = p;
        this.canvas.classList.add('is-drawing');
        // Dot so user knows the click registered
        this.ctx.beginPath();
        this.ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
        this.ctx.fillStyle = this.color;
        this.ctx.fill();
      } else {
        // Second click — commit shape
        const dx = p.x - this.placeStart!.x;
        const dy = p.y - this.placeStart!.y;
        if (Math.abs(dx) >= 5 && Math.abs(dy) >= 5) {
          const shape: Shape = {
            id: uid(),
            type: this.tool as Shape['type'],
            x: dx >= 0 ? this.placeStart!.x : p.x,
            y: dy >= 0 ? this.placeStart!.y : p.y,
            w: Math.abs(dx), h: Math.abs(dy),
            color: this.color,
          };
          this.shapes.push(shape);
          this.selectedId = shape.id;
        }
        this.placing = false;
        this.placeStart = null;
        this.canvas.classList.remove('is-drawing');
        this.redraw();
      }
    }
  };

  private onRightClick = (e: MouseEvent) => {
    e.preventDefault();
    if (this.placing) {
      this.placing = false;
      this.placeStart = null;
      this.canvas.classList.remove('is-drawing');
      this.redraw();
    }
    this.selectedId = null;
    this.redraw();
  };

  private onKey = (e: KeyboardEvent) => {
    if ((e.key === 'Delete' || e.key === 'Backspace') && this.selectedId) {
      // Only delete if not typing in an input
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;
      this.shapes = this.shapes.filter(s => s.id !== this.selectedId);
      this.selectedId = null;
      this.redraw();
    }
    if (e.key === 'Escape') {
      if (this.placing) {
        this.placing = false;
        this.placeStart = null;
        this.canvas.classList.remove('is-drawing');
        this.redraw();
      }
      this.selectedId = null;
      this.redraw();
    }
  };

  // ── Drag / resize logic ─────────────────────────────────────

  private applyDrag(p: { x: number; y: number }) {
    const ds = this.dragState!;
    const shape = this.shapes.find(s => s.id === ds.shapeId);
    if (!shape) return;
    const dx = p.x - ds.startMouse.x;
    const dy = p.y - ds.startMouse.y;
    const { x: ox, y: oy, w: ow, h: oh } = ds.startShape;

    if (ds.type === 'move') {
      shape.x = ox + dx;
      shape.y = oy + dy;
    } else if (ds.type === 'resize' && ds.handle) {
      const hk = ds.handle;
      let nx = ox, ny = oy, nw = ow, nh = oh;
      if (hk.includes('e')) nw = Math.max(MIN_SIZE, ow + dx);
      if (hk.includes('s')) nh = Math.max(MIN_SIZE, oh + dy);
      if (hk.includes('w')) { nx = ox + dx; nw = Math.max(MIN_SIZE, ow - dx); }
      if (hk.includes('n')) { ny = oy + dy; nh = Math.max(MIN_SIZE, oh - dy); }
      shape.x = nx; shape.y = ny; shape.w = nw; shape.h = nh;
    }
    this.redraw();
  }
}
